/* ============================================================
   CHRONCES — <cr-bundle-builder>
   Compose ton pack : sélection de N motifs à prix fixe.

   MODE ACTUEL (sans app) : on ajoute UNE ligne « Pack à composer » au
   prix fixe, et les motifs choisis partent en propriétés de ligne.
   Ils apparaissent donc dans le panier, la commande et l'e-mail de
   confirmation — de quoi préparer l'envoi.

   Au-delà de N, chaque motif part sur sa propre ligne via un produit
   « supplément » de sa taille, à la moitié du prix (blocs `extra` de la
   section). Sans ces blocs, la sélection est simplement plafonnée à N.

   ⚠️ Le stock des motifs choisis n'est PAS décrémenté : ça demanderait
   un Cart Transform, donc une app (voir bundle-app/ et CLAUDE.md §18).
   ============================================================ */

import { CartLinesUpdateEvent } from '@shopify/events';
import { morphSection } from '@theme/section-renderer';
import { fetchConfig } from '@theme/utilities';
// Import relatif : le magasin est un asset du thème, pas une brique
// d'Horizon — inutile de toucher à l'importmap de `snippets/scripts.liquid`.
import { readPicks, writePicks } from './cr-bundle-store.js';

const SECTION_ID = 'cart-drawer-section';

/** Insensible à la casse et aux accents : « koi » doit trouver « Koï ». */
const norm = (value) =>
  (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

class CrBundleBuilder extends HTMLElement {
  connectedCallback() {
    this.max = parseInt(this.dataset.max || '6', 10);
    this.picks = [];
    // Vrai dès le premier geste du client : sa sélection prime alors sur
    // le stockage. Vrai aussi quand les 125 motifs sont dans la grille.
    this.userTouched = false;
    this.catalogueComplete = false;
    this.activeCollection = '';
    this.activeSize = '';
    this.sort = 'default';

    this.grid = this.querySelector('[data-cr-bb-grid]');
    this.slotsWrap = this.querySelector('[data-cr-bb-slots]');
    this.cta = this.querySelector('[data-cr-bb-cta]');
    this.countEl = this.querySelector('[data-cr-bb-count]');
    this.priceEl = this.querySelector('[data-cr-bb-price]');
    this.regularEl = this.querySelector('[data-cr-bb-regular]');
    this.savingEl = this.querySelector('[data-cr-bb-saving]');
    this.progressEl = this.querySelector('[data-cr-bb-progress]');
    this.promoEl = this.querySelector('[data-cr-bb-promo]');
    this.flash = this.querySelector('[data-cr-bb-flash]');
    this.emptyEl = this.querySelector('[data-cr-bb-empty]');
    this.resultCount = this.querySelector('[data-cr-bb-results]');
    this.insurance = this.querySelector('[data-cr-bb-ins]');
    this.panel = this.querySelector('[data-cr-bb-panel]');
    this.filterToggle = this.querySelector('[data-cr-bb-filter-toggle]');
    this.activeBadge = this.querySelector('[data-cr-bb-active]');
    this.search = this.querySelector('[data-cr-bb-search]');
    this.sortSelect = this.querySelector('[data-cr-bb-sort]');

    // Les motifs « supplément », un par taille. Vide = pas d'extras possibles.
    try {
      this.extras = JSON.parse(this.dataset.extras || '[]');
    } catch {
      this.extras = [];
    }

    // Nom d'univers affiché sur chaque carte : le titre du chip dont le motif
    // porte le handle. On lit les chips plutôt que de dupliquer la liste.
    this.universes = Array.from(this.querySelectorAll('[data-cr-bb-chip]'))
      .filter((chip) => chip.dataset.collection)
      .map((chip) => ({ handle: chip.dataset.collection, title: chip.textContent.trim() }));

    this.collectCards();

    // Reprend la sélection commencée sur la landing publicitaire.
    this.restorePicks();

    this.cta?.addEventListener('click', this.onAdd);

    this.insurance?.addEventListener('click', () => {
      this.insurance.classList.toggle('is-checked');
      const box = this.insurance.querySelector('[data-cr-bb-ins-box]');
      if (box) box.textContent = this.insuranceChecked ? '✓' : '';
      this.insurance.setAttribute('aria-pressed', String(this.insuranceChecked));
      this.render();
    });

    this.search?.addEventListener('input', () => this.applyFilters());
    this.sortSelect?.addEventListener('change', () => {
      this.sort = this.sortSelect.value;
      this.applySort();
      // La barre de tri est collante : elle reste sous le curseur après le
      // choix. Un <select> encore actif capte la molette au lieu de laisser
      // défiler la page — d'où l'impression que le défilement se bloque.
      this.sortSelect.blur();
    });

    this.bar = this.querySelector('[data-cr-bb-bar]');
    this.filterToggle?.addEventListener('click', () => {
      // En desktop le panneau défile avec la page : une fois sorti de l'écran,
      // un clic sur « Filtrer » le refermerait sans qu'on le voie — il le
      // ramène. En mobile il est épinglé, donc toujours visible : le bouton
      // doit alors simplement fermer.
      const epingle = this.panel && getComputedStyle(this.panel.parentElement).position === 'fixed';
      if (this.panel && !this.panel.hidden && !epingle && !this.panelInView()) {
        this.placePanel();
        return;
      }
      this.togglePanel();
    });
    // Un changement de largeur déplace la barre : le panneau ouvert suit.
    window.addEventListener('resize', () => {
      if (this.panel && !this.panel.hidden) this.placePanel();
    });
    this.bindChips('[data-cr-bb-chip]', 'activeCollection');
    this.bindChips('[data-cr-bb-size]', 'activeSize');
    this.querySelectorAll('[data-cr-bb-clear]').forEach((btn) =>
      btn.addEventListener('click', () => this.clearFilters())
    );

    this.bindTray();
    this.bindTrayHeight();
    this.render();
    this.applyFilters();
    this.loadRest();
  }

  /**
   * Barre du bas repliable. Elle arrive REPLIÉE (une ligne), se déplie à
   * chaque motif ajouté puis se replie seule après `tray_autohide` secondes.
   * Un dépliage MANUEL ne se replie jamais tout seul : c'est une intention de
   * l'utilisateur, pas une notification.
   */
  bindTray() {
    this.tray = this.querySelector('[data-cr-bb-tray]');
    this.trayToggle = this.querySelector('[data-cr-bb-tray-toggle]');
    this.autoHide = parseInt(this.dataset.trayAutohide || '5', 10) * 1000;
    if (!this.tray) return;

    this.trayToggle?.addEventListener('click', () => {
      this.setTray(this.tray.classList.contains('cr-tray--collapsed'), { manual: true });
    });

    // Toucher à la barre (assurance, retrait d'un motif) annule le repli
    // automatique : on ne se referme pas sous les doigts de quelqu'un.
    this.tray.addEventListener('pointerdown', () => clearTimeout(this.trayTimer));
  }

  setTray(open, { manual = false } = {}) {
    if (!this.tray) return;
    clearTimeout(this.trayTimer);
    this.tray.classList.toggle('cr-tray--collapsed', !open);
    this.trayToggle?.setAttribute('aria-expanded', String(open));
    if (open && !manual && this.autoHide > 0) {
      this.trayTimer = setTimeout(() => this.setTray(false), this.autoHide);
    }
    // La hauteur change : la réserve de bas de page doit suivre, y compris
    // pendant la transition CSS.
    this.measureTray?.();
    setTimeout(() => this.measureTray?.(), 320);
  }

  /**
   * La barre du bas est fixe : la page doit lui réserver sa hauteur, sinon la
   * fin du contenu passe dessous. Cette hauteur n'est pas figée — elle change
   * avec le repli, le bandeau de promo, le retour à la ligne du texte
   * d'assurance et le passage en mobile. On la mesure plutôt que de la deviner.
   */
  bindTrayHeight() {
    const tray = this.querySelector('.cr-tray');
    const root = this.closest('.cr-bb');
    if (!tray || !root) return;

    // Appelée aussi à chaque rendu : la barre grandit quand les emplacements
    // se remplissent (libellés de taille) ou que le bandeau de promo s'ouvre,
    // et on ne veut pas dépendre du seul ResizeObserver pour ça.
    this.measureTray = () => root.style.setProperty('--cr-tray-height', `${Math.ceil(tray.offsetHeight)}px`);
    const apply = this.measureTray;
    apply();
    window.addEventListener('resize', apply);
    if ('ResizeObserver' in window) {
      this.trayObserver = new ResizeObserver(apply);
      this.trayObserver.observe(tray);
    }
  }

  disconnectedCallback() {
    this.trayObserver?.disconnect();
  }

  bindChips(selector, stateKey) {
    this.querySelectorAll(selector).forEach((chip) => {
      chip.addEventListener('click', () => {
        this.querySelectorAll(selector).forEach((c) => c.setAttribute('aria-pressed', 'false'));
        chip.setAttribute('aria-pressed', 'true');
        this[stateKey] = chip.dataset.collection || '';
        this.applyFilters();
      });
    });
  }

  togglePanel(force) {
    if (!this.panel) return;
    const open = force ?? this.panel.hidden;
    this.panel.hidden = !open;
    this.filterToggle?.setAttribute('aria-expanded', String(open));
    this.filterToggle?.classList.toggle('is-open', open);
    this.panel.parentElement?.classList.toggle('is-open', open);

    if (open) {
      this.placePanel();
      // En mobile le panneau est ÉPINGLÉ tant qu'il est ouvert : on suit donc
      // le bas de la barre à chaque défilement. Passif : aucun blocage du
      // défilement, et la mesure est faite au rythme des frames.
      window.addEventListener('scroll', this.trackPanel, { passive: true });
      document.addEventListener('click', this.onOutsideClick);
      document.addEventListener('keydown', this.onPanelKeydown);
    } else {
      window.removeEventListener('scroll', this.trackPanel);
      document.removeEventListener('click', this.onOutsideClick);
      document.removeEventListener('keydown', this.onPanelKeydown);
    }
  }

  trackPanel = () => {
    if (this.panelFrame) return;
    this.panelFrame = requestAnimationFrame(() => {
      this.panelFrame = null;
      this.placePanel();
    });
  };

  /** Un clic hors du panneau le referme — c'est la sortie attendue d'un menu. */
  onOutsideClick = (event) => {
    if (this.panel.hidden) return;
    if (this.panel.contains(event.target) || this.filterToggle?.contains(event.target)) return;
    this.togglePanel(false);
  };

  onPanelKeydown = (event) => {
    if (event.key === 'Escape') {
      this.togglePanel(false);
      this.filterToggle?.focus();
    }
  };

  /**
   * Le panneau s'ouvre LÀ OÙ ON EST — juste sous la barre collante telle
   * qu'elle est à l'écran — et non en haut de la grille. Il est posé en absolu
   * dans le document, donc il défile ensuite avec la page au lieu de rester
   * figé par-dessus les motifs. Les deux demandes tiennent ainsi ensemble.
   */
  panelInView() {
    if (!this.panel) return false;
    const r = this.panel.getBoundingClientRect();
    return r.bottom > 0 && r.top < window.innerHeight;
  }

  placePanel() {
    const wrap = this.panel?.parentElement;
    const root = this.closest('.cr-bb');
    if (!wrap || !root || !this.bar) return;
    const barBottom = this.bar.getBoundingClientRect().bottom;
    // Desktop : position ABSOLUE dans la page — le panneau défile avec elle.
    wrap.style.setProperty('--cr-panel-top', `${Math.round(barBottom - root.getBoundingClientRect().top)}px`);
    // Mobile : position FIXE — il reste épinglé sous la barre tant qu'il est
    // ouvert, et se ferme au bouton, au clic extérieur ou à Échap.
    wrap.style.setProperty('--cr-panel-fixed-top', `${Math.round(barBottom)}px`);
  }

  clearFilters() {
    this.activeCollection = '';
    this.activeSize = '';
    if (this.search) this.search.value = '';
    this.querySelectorAll('[data-cr-bb-chip], [data-cr-bb-size]').forEach((chip) => {
      chip.setAttribute('aria-pressed', String(!chip.dataset.collection));
    });
    this.applyFilters();
  }

  collectCards() {
    this.cards = Array.from(this.querySelectorAll('[data-cr-bb-card]'));
    this.items = Array.from(this.querySelectorAll('[data-cr-bb-item]'));

    this.items.forEach((item, index) => {
      // L'ordre d'origine de la collection : c'est lui que « Populaire »
      // restitue, puisque le tri réordonne le DOM.
      if (!item.dataset.rank) item.dataset.rank = String(index);
      // Liquid ne sait pas décomposer les accents : on normalise ici, sinon
      // « koi » ne trouverait « Koï » que sur les cartes construites en JS.
      item.dataset.search = norm(item.dataset.title || item.dataset.search);
    });

    // `bound` évite qu'un second passage de liaison (après le chargement du
    // complément de catalogue) rebranche ces cartes : deux écouteurs sur la
    // même carte annulaient la sélection au clic suivant.
    this.cards.forEach((card) => {
      if (card.dataset.bound) return;
      card.dataset.bound = '1';
      card.addEventListener('click', () => this.addPick(card));
      // Le « − » est un frère du bouton de carte, pas un enfant : un bouton
      // dans un bouton n'est pas du HTML valide.
      card
        .closest('[data-cr-bb-item]')
        ?.querySelector('[data-cr-bb-minus]')
        ?.addEventListener('click', () => this.removeOne(card.dataset.variantId));
    });
    this.cards.forEach((card) => this.paintUnivers(card));
  }

  /** Le nom de l'univers n'est pas rendu côté serveur : il dépend des chips. */
  paintUnivers(card) {
    const target = card.querySelector('[data-cr-bb-univers]');
    if (!target || target.textContent.trim()) return;
    const handles = (card.closest('[data-cr-bb-item]')?.dataset.collections || '').split(' ');
    const match = this.universes.find((u) => handles.includes(u.handle));
    if (match) target.textContent = match.title;
  }

  /**
   * Liquid plafonne `collection.products` à 50 éléments, et `paginate` n'est
   * pas autorisé sur une collection référencée par réglage. On rend donc les
   * 50 premiers côté serveur (premier affichage rapide) puis on complète
   * depuis `?view=cr-bb-data`, un template de collection qui, lui, a le droit
   * de paginer — et qui renvoie les collections d'appartenance et les
   * dimensions, absentes de `/products.json`.
   */
  async loadRest() {
    const handle = this.dataset.collection;
    const total = parseInt(this.dataset.total || '0', 10);
    if (!handle || this.items.length >= total) return;

    const url = (page) => `/collections/${handle}?view=cr-bb-data&page=${page}`;
    const seen = new Set(this.cards.map((c) => c.dataset.variantId));

    try {
      // 🚨 Surtout PAS d'en-tête `Accept: application/json` : Shopify
      // court-circuite alors le template et renvoie sa propre réponse JSON de
      // collection (id, titre, description… et aucun produit). Vérifié en réel.
      const first = await fetch(url(1));
      if (!first.ok) throw new Error(`HTTP ${first.status}`);
      const data = await first.json();

      const pages = [data];
      const rest = [];
      for (let p = 2; p <= (data.pages || 1); p += 1) rest.push(fetch(url(p)).then((r) => r.json()));
      pages.push(...(await Promise.all(rest)));

      const fragment = document.createDocumentFragment();
      pages.forEach(({ products }) => {
        (products || []).forEach((product) => {
          if (seen.has(String(product.v))) return;
          seen.add(String(product.v));
          fragment.appendChild(this.buildCard(product));
        });
      });
      this.grid.appendChild(fragment);
    } catch (error) {
      // Le constructeur reste utilisable avec les 50 motifs rendus côté serveur.
      console.warn('[cr-bundle-builder] catalogue partiel', error);
    }

    this.collectCards();

    // 🚨 Deuxième passe de restauration, obligatoire. Seuls 50 motifs sont
    // rendus côté serveur : une sélection portant sur un motif chargé ici
    // aurait été silencieusement écartée par la première passe, faute de
    // carte à laquelle la rattacher. La méthode ignore ce qui est déjà pris.
    this.catalogueComplete = true;
    this.restorePicks();

    this.applySort();
    this.applyFilters();
    this.render();
  }

  buildCard(product) {
    const item = document.createElement('div');
    item.className = 'cr-bb__item';
    item.setAttribute('data-cr-bb-item', '');
    item.dataset.collections = product.c || '';
    item.dataset.search = norm(product.t);
    item.dataset.price = product.p;
    item.dataset.title = product.t;

    const image = product.i || '';
    const thumb = image ? image.replace(/width=\d+/, 'width=200') : '';
    const size = product.d || '';

    item.innerHTML = `
      <button type="button" class="cr-bb__minus" data-cr-bb-minus aria-label="Retirer un exemplaire">−</button>
      <button type="button" class="cr-bb__card" data-cr-bb-card
        data-variant-id="${product.v}"
        data-title="${this.escape(product.t)}"
        data-price="${product.p}"
        data-image="${thumb}"
        data-size="${this.escape(size)}"
        aria-pressed="false">
        <span class="cr-bb__badge" data-cr-bb-badge aria-hidden="true">+</span>
        <span class="cr-bb__media">${image ? `<img src="${image}" alt="" loading="lazy">` : ''}</span>
        <span class="cr-bb__name">${this.escape(product.t)}</span>
        <span class="cr-bb__collection" data-cr-bb-univers></span>
        <span class="cr-bb__foot">
          ${size ? `<span class="cr-bb__size">⤢ ${this.escape(size)}</span>` : ''}
          <span class="cr-bb__price" data-cr-bb-card-price>${this.formatMoney(product.p)}</span>
        </span>
      </button>`;
    return item;
  }

  escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
  }

  get insuranceChecked() {
    return Boolean(this.insurance?.classList.contains('is-checked'));
  }

  get extrasEnabled() {
    return this.extras.length > 0;
  }

  /** Le supplément qui correspond à la taille du motif, s'il existe. */
  extraFor(card) {
    const handles = (card.closest('[data-cr-bb-item]')?.dataset.collections || '').split(' ');
    return this.extras.find((e) => handles.includes(e.c)) || null;
  }

  /**
   * Un clic AJOUTE un exemplaire — un même motif peut être pris plusieurs fois
   * dans le pack. On retire par le « − » de la carte ou par la croix de
   * l'emplacement, jamais en recliquant : sinon prendre deux fois le même
   * motif serait impossible.
   */
  addPick(card) {
    this.userTouched = true;
    const extra = this.picks.length >= this.max ? this.extraFor(card) : null;
    // Plein, et pas de supplément pour cette taille : on n'ajoute pas.
    if (this.picks.length >= this.max && !extra) return;

    this.picks.push({
      id: card.dataset.variantId,
      title: card.dataset.title,
      image: card.dataset.image,
      size: card.dataset.size || '',
      price: parseInt(card.dataset.price || '0', 10),
      extra,
    });
    // Un ajout mérite d'être vu : la barre se déplie, puis se replie seule.
    this.setTray(true);
    this.render();
  }

  /**
   * Reprend la sélection laissée par le mini-builder de la landing.
   *
   * 🚨 On ne fait PAS confiance aux données stockées pour l'affichage : on
   * ne garde que l'identifiant de variante et on reconstruit le pick depuis
   * la CARTE rendue par le serveur. Un prix, un titre ou une taille ont pu
   * changer depuis la visite précédente, et le stockage n'en sait rien —
   * afficher un prix périmé au moment d'ajouter au panier serait pire que
   * de perdre la sélection.
   *
   * Un motif qui n'existe plus, ou qui est épuisé, disparaît simplement :
   * sa carte n'est pas dans la grille.
   */
  restorePicks() {
    // Dès que le client a cliqué, sa sélection à l'écran fait autorité :
    // on n'écrase jamais ce qu'il vient de faire par ce qui était stocké.
    if (this.userTouched) return;

    const stored = readPicks();
    if (!stored.length) return;

    // Reconstruction COMPLÈTE plutôt qu'un ajout incrémental : la méthode
    // tourne deux fois (avant et après le chargement du reste du catalogue)
    // et doit donner le même résultat quel que soit le nombre de passages.
    // 🚨 Chercher dans `[data-cr-bb-card]`, PAS dans `this.items` : ce dernier
    // contient les conteneurs `.cr-bb__item` qui servent au filtrage et au
    // tri, et qui ne portent NI l'identifiant de variante NI l'image. La
    // carte est leur enfant. Piège coûteux : `this.items.find(...)` ne lève
    // rien, il ne trouve simplement jamais — la restauration échouait en
    // silence et vidait la sélection.
    const cards = Array.from(this.querySelectorAll('[data-cr-bb-card]'));
    const rebuilt = [];
    stored.slice(0, this.max).forEach((p) => {
      const card = cards.find((c) => c.dataset.variantId === String(p.id));
      if (!card) return;
      rebuilt.push({
        id: card.dataset.variantId,
        title: card.dataset.title,
        image: card.dataset.image,
        size: card.dataset.size || '',
        price: parseInt(card.dataset.price || '0', 10),
        extra: null,
      });
    });
    this.picks = rebuilt;
  }

  /** Retire le DERNIER exemplaire de ce motif (pas tous les exemplaires). */
  removeOne(id) {
    this.userTouched = true;
    const index = this.picks.map((p) => p.id).lastIndexOf(id);
    if (index > -1) this.picks.splice(index, 1);
    this.render();
  }

  /** Retire l'exemplaire à cette position — les doublons partagent leur id. */
  removeAt(index) {
    this.userTouched = true;
    this.picks.splice(index, 1);
    this.render();
  }

  applyFilters() {
    const term = norm(this.search?.value.trim());
    let visible = 0;

    this.items.forEach((item) => {
      const handles = (item.dataset.collections || '').split(' ');
      const matchesCollection = !this.activeCollection || handles.includes(this.activeCollection);
      const matchesSize = !this.activeSize || handles.includes(this.activeSize);
      const matchesTerm = !term || (item.dataset.search || '').includes(term);
      const show = matchesCollection && matchesSize && matchesTerm;
      item.hidden = !show;
      if (show) visible += 1;
    });

    if (this.resultCount) this.resultCount.textContent = visible;
    if (this.emptyEl) this.emptyEl.hidden = visible > 0;

    const active = (this.activeCollection ? 1 : 0) + (this.activeSize ? 1 : 0) + (term ? 1 : 0);
    if (this.activeBadge) {
      this.activeBadge.hidden = active === 0;
      this.activeBadge.textContent = active;
    }
    this.filterToggle?.classList.toggle('is-active', active > 0);
    this.querySelectorAll('[data-cr-bb-clear]').forEach((btn) => {
      if (btn.closest('[data-cr-bb-panel]')) btn.hidden = active === 0;
    });
  }

  /** Le tri réordonne le DOM : les filtres restent valables tels quels. */
  applySort() {
    if (!this.grid) return;
    const items = Array.from(this.grid.children);
    const by = {
      'price-asc': (a, b) => Number(a.dataset.price) - Number(b.dataset.price),
      'price-desc': (a, b) => Number(b.dataset.price) - Number(a.dataset.price),
      az: (a, b) => (a.dataset.title || '').localeCompare(b.dataset.title || '', 'fr'),
    }[this.sort];

    if (by) {
      items.sort(by);
    } else {
      // « Populaire » = l'ordre de la collection, qu'on retrouve par l'index
      // posé au premier rendu.
      items.sort((a, b) => Number(a.dataset.rank || 0) - Number(b.dataset.rank || 0));
    }
    // 🚨 Surtout pas un DocumentFragment ici : y verser les cartes les détache
    // toutes, la grille se vide, la page perd ~14 000 px de hauteur et le
    // navigateur ramène le défilement à la nouvelle limite. `append` déplace
    // les nœuds dans leur parent — la grille n'est jamais vide.
    this.grid.append(...items);
  }

  formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency: this.dataset.currency || 'EUR',
      })
        .format(cents / 100)
        .replace(/[\s  ]+(?=\D*$)/, '');
    } catch {
      return `${(cents / 100).toFixed(2).replace('.', ',')}€`;
    }
  }

  render() {
    const complete = this.picks.length >= this.max;
    const extras = this.picks.slice(this.max);

    // La sélection est rangée à CHAQUE rendu, pas seulement à l'ajout :
    // retraits, croix d'emplacement et vidage après achat passent tous par
    // ici, et un seul point d'écriture ne peut pas se désynchroniser.
    //
    // 🚨 Mais PAS tant que le catalogue est incomplet et que le client n'a
    // rien touché : les 50 premiers motifs sont rendus côté serveur, les 75
    // autres arrivent après. Écrire à ce moment-là amputerait le stockage
    // d'un motif choisi sur la landing mais pas encore présent dans la
    // grille — et la seconde passe de restauration ne le retrouverait plus.
    if (this.catalogueComplete || this.userTouched) writePicks(this.picks);

    // Emplacements : les N du pack, plus un par motif en supplément.
    if (this.slotsWrap) {
      this.slotsWrap.innerHTML = '';
      const count = Math.max(this.max, this.picks.length);
      for (let i = 0; i < count; i += 1) {
        const pick = this.picks[i];
        const isExtra = i >= this.max;
        const slot = document.createElement('div');
        slot.className = `cr-slot${pick ? ' cr-slot--filled' : ''}${isExtra ? ' cr-slot--extra' : ''}`;
        slot.innerHTML = pick
          ? `<div class="cr-slot__box"><img src="${pick.image}" alt="">
               ${isExtra ? '<span class="cr-slot__off">−50%</span>' : ''}
               <button type="button" class="cr-slot__remove" aria-label="Retirer ${this.escape(pick.title)}">×</button>
             </div>
             <div class="cr-slot__label" title="${this.escape(pick.title)}">${isExtra ? '−50 %' : this.escape(pick.size || pick.title)}</div>`
          : `<div class="cr-slot__box"><span class="cr-slot__n">${i + 1}</span></div>`;
        if (pick) {
          // Par POSITION : deux exemplaires du même motif partagent leur id,
          // filtrer par id les retirerait tous les deux.
          slot.querySelector('.cr-slot__remove').addEventListener('click', () => this.removeAt(i));
        }
        this.slotsWrap.appendChild(slot);
      }
    }

    // Cartes : badge numéroté, demi-prix affiché sur les extras, et carte
    // désactivée quand la sélection est pleine sans supplément possible.
    this.cards.forEach((card) => {
      const id = card.dataset.variantId;
      const ids = this.picks.map((p) => p.id);
      const count = ids.filter((x) => x === id).length;
      const picked = count > 0;
      const lastIndex = ids.lastIndexOf(id);
      const isExtra = picked && lastIndex >= this.max;
      const extra = this.extrasEnabled ? this.extraFor(card) : null;

      card.setAttribute('aria-pressed', String(picked));
      card.classList.toggle('cr-bb__card--extra', isExtra);
      // Plein : on ne peut ajouter que si un supplément existe pour cette
      // taille — y compris un exemplaire de plus d'un motif déjà pris.
      card.disabled = complete && !extra;
      card.closest('[data-cr-bb-item]')?.classList.toggle('is-picked', picked);

      const badge = card.querySelector('[data-cr-bb-badge]');
      if (badge) {
        if (!picked) badge.textContent = '+';
        else badge.textContent = count > 1 ? `×${count}` : String(ids.indexOf(id) + 1);
      }

      // Le prix affiché est celui du PROCHAIN clic : à sélection pleine, un
      // exemplaire de plus part au demi-tarif du supplément.
      const price = card.querySelector('[data-cr-bb-card-price]');
      if (price) {
        const unit = parseInt(card.dataset.price || '0', 10);
        price.textContent = complete && extra ? this.formatMoney(extra.p) : this.formatMoney(unit);
      }
    });

    // Totaux
    const bundlePrice = parseInt(this.dataset.price || '0', 10);
    const insurancePrice = this.insuranceChecked ? parseInt(this.dataset.insurancePrice || '0', 10) : 0;
    const extrasPaid = extras.reduce((sum, p) => sum + (p.extra ? p.extra.p : 0), 0);
    const regular = this.picks.reduce((sum, p) => sum + p.price, 0) + insurancePrice;
    const total = bundlePrice + extrasPaid + insurancePrice;

    if (this.countEl) {
      if (!complete) {
        this.countEl.textContent = `${this.picks.length} / ${this.max} sélectionnés`;
      } else if (extras.length > 0) {
        this.countEl.textContent = `${this.max} + ${extras.length} en promo −50 %`;
      } else {
        this.countEl.textContent = 'Pack complet !';
      }
    }
    if (this.priceEl) this.priceEl.textContent = this.formatMoney(total);

    // L'économie s'affiche dès qu'elle est RÉELLE, sans attendre le pack
    // complet (décision utilisateur, 2026-08-03) : le client doit voir
    // l'affaire pendant qu'il compose, c'est ce qui le fait continuer.
    // `regular > total` suffit comme garde-fou — sur un ou deux motifs la
    // somme est inférieure au prix du pack, donc rien ne s'affiche et on
    // n'annonce jamais une remise qui n'existe pas.
    const showSaving = regular > total;
    if (this.savingEl) {
      this.savingEl.hidden = !showSaving;
      if (showSaving) this.savingEl.textContent = `− ${this.formatMoney(regular - total)}`;
    }
    if (this.regularEl) {
      this.regularEl.hidden = !showSaving;
      if (showSaving) this.regularEl.textContent = this.formatMoney(regular);
    }
    if (this.promoEl) this.promoEl.hidden = !(complete && this.extrasEnabled);

    if (this.progressEl) {
      this.progressEl.style.width = `${Math.min(100, (this.picks.length / this.max) * 100)}%`;
    }
    if (this.cta) {
      this.cta.disabled = !complete;
      this.cta.textContent = complete
        ? this.dataset.ctaReady || 'Ajouter au panier'
        : `Choisis encore ${this.max - this.picks.length}`;
    }

    this.measureTray?.();
  }

  onAdd = async () => {
    if (this.busy || this.picks.length < this.max) return;
    this.busy = true;
    this.classList.add('is-busy');

    // Les motifs choisis voyagent en propriétés de ligne : c'est ce qui
    // permet de préparer la commande sans app.
    const inPack = this.picks.slice(0, this.max);
    const extras = this.picks.slice(this.max);

    const properties = {};
    inPack.forEach((p, i) => {
      properties[`Motif ${i + 1}`] = p.title;
    });
    // Propriété technique cachée (le préfixe `_` la masque au client) :
    // c'est ce que lit la Function Cart Transform pour éclater la ligne en
    // ses motifs réels. Inutile tant que l'app n'est pas installée, mais
    // inoffensive — et évite d'avoir à modifier le thème le jour venu.
    properties._variants = inPack.map((p) => `gid://shopify/ProductVariant/${p.id}`).join(',');

    const items = [{ id: Number(this.dataset.variantId), quantity: 1, properties }];

    // Un extra = une ligne du produit « supplément » de sa taille, portant le
    // nom du motif. Deux lignes aux propriétés différentes ne fusionnent pas :
    // chaque motif reste donc lisible sur la commande.
    extras.forEach((p) => {
      if (!p.extra) return;
      items.push({
        id: Number(p.extra.v),
        quantity: 1,
        properties: { Motif: p.title, _variants: `gid://shopify/ProductVariant/${p.id}` },
      });
    });

    const insuranceId = this.dataset.insuranceVariantId;
    if (this.insuranceChecked && insuranceId) items.push({ id: Number(insuranceId), quantity: 1 });

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ id: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const body = JSON.stringify({ items, sections: SECTION_ID, sections_url: window.location.pathname });
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));

      if (!response.ok) {
        let reason = `HTTP ${response.status}`;
        try {
          const err = await response.clone().json();
          reason = err.description || err.message || reason;
        } catch {
          /* réponse HTML */
        }
        throw new Error(reason);
      }

      const json = await response.json();
      const cart = await (await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } })).json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: json.sections, itemCount: cart.item_count, source: 'cr-bundle-builder', didError: false },
      });

      if (json.sections?.[SECTION_ID]) morphSection(SECTION_ID, json.sections[SECTION_ID], { mode: 'hydration' });

      this.showFlash(this.dataset.flashOk || '✓ Pack ajouté au panier — bien joué !');
      this.picks = [];
      this.render();
      document.querySelector('#cart-drawer')?.open?.();
    } catch (error) {
      console.error('[cr-bundle-builder]', error);
      deferred.reject(error);
      this.showFlash("Ajout impossible pour le moment — réessaie dans un instant.");
    } finally {
      this.classList.remove('is-busy');
      this.busy = false;
    }
  };

  showFlash(message) {
    if (!this.flash) return;
    this.flash.textContent = message;
    this.flash.hidden = false;
    clearTimeout(this.flashTimer);
    this.flashTimer = setTimeout(() => { this.flash.hidden = true; }, 4000);
  }
}

if (!customElements.get('cr-bundle-builder')) customElements.define('cr-bundle-builder', CrBundleBuilder);
